import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useHistory, useParams } from 'react-router-dom';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { DefaultTitle } from '../../../_metronic/layout/components/header/page-title/DefaultTitle';
import folhaPagamentoGlobalService, {
    IFolhaGlobalRubricaFilter,
    IFolhaGlobalRubricaItem,
    IFolhaGlobalResumo,
} from '../../services/folhaPagamentoGlobalService';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const PER_PAGE = 20;

const ORIGEM_LABEL: Record<string, string> = {
    DEPARA_ADIANTAMENTO: 'De-Para · Adiantamento',
    DEPARA_PROVISAO: 'De-Para · Provisão',
    DEPARA_COMPLEMENTO: 'De-Para · Complemento',
    CATEGORIA_FALLBACK: 'Categoria (fallback)',
};

function statusInfo(r: IFolhaGlobalResumo): { label: string; color: string; bg: string } {
    if (r.arquivoErro) return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (r.arquivoProcessando) return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (r.arquivoProcessou) return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    return { label: 'AGUARDANDO', color: '#6c757d', bg: '#6c757d22' };
}

const filtroVazio: IFolhaGlobalRubricaFilter = { statusMatch: undefined };

function StatCard({ label, value, color }: { label: string; value: number | string; color: string }) {
    return (
        <div className='border rounded px-4 py-3' style={{ minWidth: 140 }}>
            <div className='text-muted fs-8 fw-semibold text-uppercase'>{label}</div>
            <div className='fs-2 fw-bold' style={{ color }}>{value}</div>
        </div>
    );
}

function FolhaPagamentoGlobalDetalhe() {
    const { esteiraId, arquivoOperacaoId } = useParams<{ esteiraId: string; arquivoOperacaoId: string }>();
    const history = useHistory();
    const { classes } = useLayout();

    const esteiraIdNum = Number(esteiraId);
    const arquivoIdNum = Number(arquivoOperacaoId);

    const [resumo, setResumo] = useState<IFolhaGlobalResumo | null>(null);
    const [loadingResumo, setLoadingResumo] = useState(true);

    const [items, setItems] = useState<IFolhaGlobalRubricaItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [filtros, setFiltros] = useState<IFolhaGlobalRubricaFilter>(filtroVazio);

    useEffect(() => {
        setLoadingResumo(true);
        folhaPagamentoGlobalService.obterResumo(arquivoIdNum)
            .then(setResumo)
            .finally(() => setLoadingResumo(false));
    }, [arquivoIdNum]);

    const carregar = useCallback((pg: number, f: IFolhaGlobalRubricaFilter) => {
        setLoading(true);
        folhaPagamentoGlobalService.listarLinhas(esteiraIdNum, { ...f, arquivoOperacaoId: arquivoIdNum, page: pg, perPage: PER_PAGE })
            .then(resp => {
                setItems(resp.items);
                setTotalPages(resp.totalPages);
                setTotal(resp.total);
                setPage(pg);
            })
            .finally(() => setLoading(false));
    }, [esteiraIdNum, arquivoIdNum]);

    useEffect(() => { carregar(1, filtroVazio); }, [carregar]);

    const setF = (patch: Partial<IFolhaGlobalRubricaFilter>) => setFiltros(prev => ({ ...prev, ...patch }));
    const onBuscar = () => carregar(1, filtros);
    const onLimpar = () => { setFiltros(filtroVazio); carregar(1, filtroVazio); };

    return (
        <>
            <PageTitle breadcrumbs={[]}>Folha de Pagamento (De-Para) — Detalhe</PageTitle>
            <div className='toolbar' id='kt_toolbar'>
                <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                    <DefaultTitle />
                </div>
            </div>

            <div className='card mb-4'>
                <div className='card-body'>
                    <button className='btn btn-sm btn-light mb-4' onClick={() => history.push('/andamento-contas')}>
                        ‹ Voltar
                    </button>

                    {loadingResumo || !resumo ? (
                        <div className='d-flex justify-content-center py-6'>
                            <span className='spinner-border spinner-border-sm text-primary me-2 mt-1' />
                            <span>Carregando...</span>
                        </div>
                    ) : (
                        <>
                            <div className='d-flex justify-content-between align-items-start flex-wrap mb-4'>
                                <div>
                                    <h4 className='mb-1'>Período {resumo.periodoReferencia || '—'}</h4>
                                </div>
                                {(() => {
                                    const st = statusInfo(resumo);
                                    return (
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, color: st.color, backgroundColor: st.bg,
                                            padding: '5px 14px', borderRadius: 6, whiteSpace: 'nowrap'
                                        }}>
                                            {st.label}
                                        </span>
                                    );
                                })()}
                            </div>

                            <div className='d-flex gap-3 flex-wrap'>
                                <StatCard label='Encontrados' value={resumo.encontrados} color='#198754' />
                                <StatCard label='Não encontrados' value={resumo.naoEncontrados} color='#dc3545' />
                                <StatCard label='Pendentes' value={resumo.pendentes} color='#3F4254' />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className='card'>
                <div className='card-body'>
                    <div className='d-flex align-items-end gap-2 flex-wrap mb-4'>
                        <div>
                            <label className='form-label fs-7 fw-semibold mb-1'>Status</label>
                            <select className='form-select form-select-sm' style={{ width: 180 }}
                                value={filtros.statusMatch ?? ''}
                                onChange={e => setF({ statusMatch: e.target.value || undefined })}>
                                <option value=''>Todos</option>
                                <option value='ENCONTRADO'>Encontrado</option>
                                <option value='NAO_ENCONTRADO'>Não encontrado</option>
                                <option value='PENDENTE'>Pendente</option>
                            </select>
                        </div>
                        <button className='btn btn-sm btn-primary' onClick={onBuscar} disabled={loading}>
                            {loading ? <span className='spinner-border spinner-border-sm' role='status' /> : 'Buscar'}
                        </button>
                        <button className='btn btn-sm btn-light' onClick={onLimpar} disabled={loading}>Limpar</button>
                        <span className='text-muted fs-8 ms-auto'>{total} registro{total !== 1 ? 's' : ''}</span>
                    </div>

                    {loading && items.length === 0 ? (
                        <div className='d-flex justify-content-center align-items-center py-10'>
                            <div className='spinner-border text-primary' role='status'>
                                <span className='visually-hidden'>Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <div className='table-responsive'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-4 gy-3'>
                                <thead>
                                    <tr className='fw-bold text-muted bg-light'>
                                        <th className='min-w-80px ps-4 rounded-start'>Código</th>
                                        <th className='min-w-200px'>Rubrica</th>
                                        <th className='min-w-100px'>Bloco</th>
                                        <th className='min-w-100px text-end'>Valor</th>
                                        <th className='min-w-120px'>Conta</th>
                                        <th className='min-w-160px'>Origem do match</th>
                                        <th className='min-w-80px text-center'>Status</th>
                                        <th className='min-w-200px text-end pe-4 rounded-end'>Motivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className='text-center text-muted py-8 fs-6'>Nenhuma rubrica encontrada.</td>
                                        </tr>
                                    ) : (
                                        items.map(item => (
                                            <tr key={item.rubricaId}>
                                                <td className='ps-4 fs-7'>{item.codigoRubrica}</td>
                                                <td className='fs-7 text-muted'>{item.descricaoRubrica || '—'}</td>
                                                <td className='fs-7'>{item.bloco}</td>
                                                <td className='text-end fs-7 fw-semibold'>{BRL.format(item.valor)}</td>
                                                <td className='fs-7 text-muted'>{item.contaCartao || '—'}</td>
                                                <td className='fs-7'>{item.origemMatch ? (ORIGEM_LABEL[item.origemMatch] || item.origemMatch) : '—'}</td>
                                                <td className='text-center'>
                                                    {item.statusMatch === 'ENCONTRADO'
                                                        ? <span style={{ color: '#198754', fontWeight: 700 }}>✔</span>
                                                        : item.statusMatch === 'NAO_ENCONTRADO'
                                                            ? <span style={{ color: '#dc3545', fontWeight: 700 }}>✘</span>
                                                            : <span className='text-muted'>—</span>}
                                                </td>
                                                <td className='text-end pe-4 fs-7 text-muted'>{item.motivo || '—'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className='d-flex justify-content-between align-items-center mt-3'>
                            <span className='fs-7 text-muted'>Página {page} de {totalPages}</span>
                            <div className='d-flex gap-1'>
                                <button className='btn btn-sm btn-light' disabled={page === 1 || loading} onClick={() => carregar(page - 1, filtros)}>‹</button>
                                <button className='btn btn-sm btn-light' disabled={page === totalPages || loading} onClick={() => carregar(page + 1, filtros)}>›</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default FolhaPagamentoGlobalDetalhe;
